'use client';

import React from 'react';
import {
  Markers,
  Marker,
  MarkerMeasurement,
} from '@/app/(authenticated)/context/MarkerContext';
import { formatMarkerName } from '@/lib/utils';

const getRelativeValue = (result: MarkerMeasurement) => {
  if (
    result.relative_value !== null &&
    result.relative_value !== undefined &&
    result.relative_value !== ('' as any)
  ) {
    return result.relative_value;
  }
  return null;
};

interface RecordRelativeValueChartProps {
  markers: Markers | null;
  // Hover tooltips with each bar's value and range. Hidden ones are still laid out, and one
  // for a long marker name reaches far past the last bar, so the landing page's preview,
  // which sizes the chart to fit exactly, turns them off.
  tooltips?: boolean;
}

export const RecordRelativeValueChart: React.FC<
  RecordRelativeValueChartProps
> = ({ markers, tooltips = true }) => {
  if (!markers || markers.length === 0) return null;

  // 1. Determine Scale
  // Fixed visual range from -0.2 to 1.2 covering the "Green Zone" [0, 1] comfortably.
  const Y_MIN = -0.2;
  const Y_MAX = 1.2;
  const SCOPE = Y_MAX - Y_MIN;

  const getPercent = (value: number) => {
    return ((value - Y_MIN) / SCOPE) * 100;
  };

  // Background Zones
  const greenTop = getPercent(1); // Top of Green Zone (Max Normal)
  const greenBottom = getPercent(0); // Bottom of Green Zone (Min Normal)
  const greenHeight = greenTop - greenBottom;

  return (
    <div className="relative w-full h-[14rem] bg-muted/10 rounded-lg border overflow-hidden">
      {/* Chart Area Wrapper for Background and Axis Labels - Reduced Height 8rem */}
      <div className="absolute inset-x-0 top-0 h-[8rem]">
        {/* Background Zones */}
        {/* High Red Zone (Above 1.0) */}
        <div
          className="absolute w-full bg-red-500/10 pointer-events-none"
          style={{
            bottom: `${greenTop}%`,
            top: 0,
          }}
        />
        {/* Green Zone (0.0 to 1.0) */}
        <div
          className="absolute w-full bg-green-500/10 pointer-events-none"
          style={{
            bottom: `${greenBottom}%`,
            height: `${greenHeight}%`,
          }}
        />
        {/* Low Red Zone (Below 0.0) */}
        <div
          className="absolute w-full bg-red-500/10 pointer-events-none"
          style={{
            bottom: 0,
            top: `${100 - greenBottom}%`,
          }}
        />

        {/* Y-Axis Labels - Absolute positioned on the left */}
        <div
          className="absolute left-2 text-[10px] text-green-700 font-medium z-10 pointer-events-none"
          style={{ bottom: `${greenTop}%`, transform: 'translateY(50%)' }}
        >
          Max
        </div>
        <div
          className="absolute left-2 text-[10px] text-green-700 font-medium z-10 pointer-events-none"
          style={{ bottom: `${greenBottom}%`, transform: 'translateY(50%)' }}
        >
          Min
        </div>
      </div>

      {/* Bars Container - Reduced Height 15rem with internal scroll */}
      {/* lib/recordChartFit.ts mirrors its padding and the bar and label sizes below: change it with them */}
      <div className="absolute inset-x-0 top-0 h-[14rem] overflow-x-auto overflow-y-hidden pl-12 pr-4 scrollbar-thin">
        <div className="flex items-start justify-start gap-1 h-full min-w-max">
          {markers.map((marker, index) => {
            const result = marker.measurements[0];
            if (!result || result.relative_value === null) return null;

            // Parse boolean from string if necessary (API quirk)
            const isOutOfRange =
              String(result.out_of_range).toLowerCase() === 'true';
            const relVal = getRelativeValue(result);

            if (relVal === null) return null;

            // Calculate Bar Height relative to the Y_MIN baseline
            const visualVal = Math.min(Math.max(relVal, Y_MIN), Y_MAX);
            const barHeightPercent = Math.max(getPercent(visualVal), 2);

            return (
              <div
                key={`${marker.reference_id}-${index}`}
                className="flex flex-col items-center justify-start h-full w-6 group relative"
              >
                {/* Bar Area: Matches Background Chart Area Height (8rem) */}
                <div className="relative w-full h-[8rem] flex items-end justify-center">
                  {/* The Bar */}
                  <div
                    className={`w-3 rounded-t-sm transition-all duration-500 ${
                      isOutOfRange ? 'bg-red-500' : 'bg-green-500'
                    }`}
                    style={{ height: `${barHeightPercent}%` }}
                  />
                </div>

                {/* Label below the bar axis - Absolute positioned relative to marker top */}
                <div className="absolute top-[8.5rem] left-1/2 rotate-45 origin-top-left text-[10px] text-foreground/70 whitespace-nowrap w-24 truncate text-left leading-tight px-1">
                  {formatMarkerName(marker.name)}
                </div>

                {/* Tooltip on hover */}
                {tooltips && (
                  <div className="absolute top-4 left-1/2 -translate-x-1/2 opacity-0 group-hover:opacity-100 bg-popover text-popover-foreground text-xs p-2 rounded shadow border transition-opacity z-20 pointer-events-none whitespace-nowrap min-w-32">
                    <div className="font-bold border-b pb-1 mb-1">
                      {formatMarkerName(marker.name)}
                    </div>
                    <div className="flex justify-between gap-4 py-0.5">
                      <span>Value:</span>
                      <span
                        className={
                          isOutOfRange ? 'text-red-500 font-medium' : ''
                        }
                      >
                        {result.value} {result.units}
                      </span>
                    </div>
                    <div className="flex justify-between gap-4 py-0.5 text-muted-foreground italic">
                      <span>Range:</span>
                      <span>
                        {result.range_min} - {result.range_max}
                      </span>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

interface InlineMarkerChartProps {
  marker: Marker;
  dates: string[];
}

export const InlineMarkerChart: React.FC<InlineMarkerChartProps> = ({
  marker,
  dates,
}) => {
  const dataPoints = dates.map(date => {
    const result = marker.measurements.find(r => r.datetime === date);
    return result;
  });

  return (
    <div className="flex items-end space-x-0.5 h-8 w-24">
      {dataPoints.map((result, idx) => {
        const relVal = result ? getRelativeValue(result) : null;

        if (!result || relVal === null) {
          return (
            <div
              key={idx}
              className="flex-1 bg-muted/30 h-full rounded-sm"
              title="No data"
            />
          );
        }

        const isOutOfRange =
          String(result.out_of_range).toLowerCase() === 'true';

        const heightPercentage = Math.min(
          Math.max((relVal ?? 0) * 100, 10),
          100
        );

        return (
          <div
            key={idx}
            className={`flex-1 rounded-sm ${
              isOutOfRange ? 'bg-red-500' : 'bg-green-500'
            }`}
            style={{ height: `${heightPercentage}%` }}
            title={`${result.datetime}\n${marker.name}\nValue: ${result.value} ${result.units}\nRange: ${result.range_min} - ${result.range_max}`}
          />
        );
      })}
    </div>
  );
};
